if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.premiumThumbnailRailSetup = false;
        this.premiumThumbnailRailSetupAttempts = 0;
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
        this.queuePremiumThumbnailRailSetup();
      }

      connectedCallback() {
        this.queuePremiumThumbnailRailSetup();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail &&
              activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);

        if (this.isPremiumThumbnailRail()) {
          this.scrollPremiumThumbnailIntoView(thumbnail);
          return;
        }

        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;
        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      isPremiumThumbnailRail() {
        return Boolean(this.closest('.pb-product-thumb-rail') && this.elements.thumbnails?.slider);
      }

      isPremiumThumbnailRailVertical() {
        return this.mql.matches && this.isPremiumThumbnailRail();
      }

      getPremiumThumbnailStep() {
        const item = this.elements.thumbnails?.slider?.querySelector('.thumbnail-list__item');
        if (!item) return 60;

        const styles = window.getComputedStyle(this.elements.thumbnails.slider);
        const gap =
          Number.parseFloat(
            (this.isPremiumThumbnailRailVertical() ? styles.rowGap : styles.columnGap) || styles.gap || '0'
          ) || 0;
        const rect = item.getBoundingClientRect();
        return (this.isPremiumThumbnailRailVertical() ? rect.height : rect.width) + gap;
      }

      queuePremiumThumbnailRailSetup() {
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => this.setupPremiumThumbnailRail());
        });
      }

      setupPremiumThumbnailRail() {
        if (this.premiumThumbnailRailSetup) return;
        if (!this.isPremiumThumbnailRail()) {
          if (this.closest('.pb-product-thumb-rail') && this.premiumThumbnailRailSetupAttempts < 8) {
            this.premiumThumbnailRailSetupAttempts += 1;
            window.setTimeout(() => this.setupPremiumThumbnailRail(), 120);
          }
          return;
        }

        this.premiumThumbnailRailSetup = true;
        const slider = this.elements.thumbnails.slider;
        const buttons = this.elements.thumbnails.querySelectorAll('.slider-button');

        buttons.forEach((button) => {
          button.addEventListener(
            'click',
            (event) => {
              event.preventDefault();
              event.stopImmediatePropagation();

              const direction = button.name === 'previous' ? -1 : 1;
              const step = Number.parseInt(button.dataset.step || '1', 10);
              const amount = direction * this.getPremiumThumbnailStep() * Math.max(1, step);
              const scrollOptions = this.isPremiumThumbnailRailVertical()
                ? { top: amount, behavior: 'smooth' }
                : { left: amount, behavior: 'smooth' };

              slider.scrollBy(scrollOptions);
              window.setTimeout(() => this.updatePremiumThumbnailRailButtons(), 180);
            },
            true
          );
        });

        slider.addEventListener('scroll', () => this.updatePremiumThumbnailRailButtons(), { passive: true });
        this.mql.addEventListener?.('change', () => this.updatePremiumThumbnailRailButtons());
        this.updatePremiumThumbnailRailButtons();
      }

      scrollPremiumThumbnailIntoView(thumbnail) {
        const slider = this.elements.thumbnails.slider;

        if (!this.isPremiumThumbnailRailVertical()) {
          const thumbnailLeft = thumbnail.offsetLeft;
          const thumbnailRight = thumbnailLeft + thumbnail.offsetWidth;
          const visibleLeft = slider.scrollLeft;
          const visibleRight = visibleLeft + slider.clientWidth;

          if (thumbnailLeft < visibleLeft) {
            slider.scrollTo({ left: thumbnailLeft, behavior: 'smooth' });
          } else if (thumbnailRight > visibleRight) {
            slider.scrollTo({ left: thumbnailRight - slider.clientWidth, behavior: 'smooth' });
          }

          window.setTimeout(() => this.updatePremiumThumbnailRailButtons(), 180);
          return;
        }

        const thumbnailTop = thumbnail.offsetTop;
        const thumbnailBottom = thumbnailTop + thumbnail.offsetHeight;
        const visibleTop = slider.scrollTop;
        const visibleBottom = visibleTop + slider.clientHeight;

        if (thumbnailTop < visibleTop) {
          slider.scrollTo({ top: thumbnailTop, behavior: 'smooth' });
        } else if (thumbnailBottom > visibleBottom) {
          slider.scrollTo({ top: thumbnailBottom - slider.clientHeight, behavior: 'smooth' });
        }

        window.setTimeout(() => this.updatePremiumThumbnailRailButtons(), 180);
      }

      updatePremiumThumbnailRailButtons() {
        if (!this.isPremiumThumbnailRail()) return;

        const slider = this.elements.thumbnails.slider;
        const previousButton = this.elements.thumbnails.querySelector('.slider-button--prev');
        const nextButton = this.elements.thumbnails.querySelector('.slider-button--next');

        if (this.isPremiumThumbnailRailVertical()) {
          const maxScrollTop = Math.max(0, slider.scrollHeight - slider.clientHeight - 1);
          previousButton?.toggleAttribute('disabled', slider.scrollTop <= 1);
          nextButton?.toggleAttribute('disabled', slider.scrollTop >= maxScrollTop);
          return;
        }

        const maxScrollLeft = Math.max(0, slider.scrollWidth - slider.clientWidth - 1);
        previousButton?.toggleAttribute('disabled', slider.scrollLeft <= 1);
        nextButton?.toggleAttribute('disabled', slider.scrollLeft >= maxScrollLeft);
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
